import { useState } from "react";
import { Button, Form, Icon, Table } from "semantic-ui-react";
import { formatRupees, isMoney } from "../../../utils/money";
import { isQuantity } from "./billGrid";

const OTHER = "other";

/* One-off charges: an item from the list with a quantity, or anything else
   typed in (description, price before GST, whether GST applies). */
export default function BillExtras({ bill, items, editable, busy, onAdd, onDelete }) {
  // The boarding lump sum lives in the meals section, not here.
  const lines = bill.lines.filter(
    (l) => (l.kind === "ITEM" && !l.serviceDate) || (l.kind === "CUSTOM" && !l.detail.lump_sum),
  );
  return (
    <div>
      {lines.length > 0 && (
        <Table compact celled unstackable size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Charge</Table.HeaderCell>
              <Table.HeaderCell>Heading</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Qty</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Price</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">GST</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Total</Table.HeaderCell>
              {editable && <Table.HeaderCell />}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {lines.map((l) => (
              <Table.Row key={l.id}>
                <Table.Cell>{l.description}</Table.Cell>
                <Table.Cell>{l.categoryName}</Table.Cell>
                <Table.Cell textAlign="right">{Number(l.quantity)}</Table.Cell>
                <Table.Cell textAlign="right">{formatRupees(l.unitPrice)}</Table.Cell>
                <Table.Cell textAlign="right">{Number(l.cgst) + Number(l.sgst) ? `${formatRupees(l.cgst)} + ${formatRupees(l.sgst)}` : "—"}</Table.Cell>
                <Table.Cell textAlign="right"><strong>{formatRupees(l.total)}</strong></Table.Cell>
                {editable && (
                  <Table.Cell collapsing>
                    <Button size="mini" basic negative icon disabled={busy} aria-label={`Remove ${l.description}`}
                      onClick={() => onDelete(l.id)}>
                      <Icon name="trash" />
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      {editable && <AddCharge items={items} busy={busy} onAdd={onAdd} />}
    </div>
  );
}

const EMPTY = { choice: "", quantity: "1", description: "", unitPrice: "", categoryName: "", gstApplicable: false };

function AddCharge({ items, busy, onAdd }) {
  const [form, setForm] = useState(EMPTY);
  const set = (key) => (_, d) => setForm((f) => ({ ...f, [key]: d.type === "checkbox" ? d.checked : d.value }));
  const other = form.choice === OTHER;
  const valid = form.choice && isQuantity(form.quantity) && Number(form.quantity) > 0
    && (!other || (form.description.trim() && isMoney(form.unitPrice)));

  const submit = async () => {
    const body = other
      ? { description: form.description.trim(), unit_price: form.unitPrice.trim(), quantity: form.quantity.trim(),
          category_name: form.categoryName.trim(), gst_applicable: form.gstApplicable }
      : { charge_item: form.choice, quantity: form.quantity.trim() };
    if (await onAdd(body)) setForm(EMPTY);
  };

  const options = [
    ...items.filter((i) => i.isActive).map((i) => ({
      key: i.id, value: i.id, text: `${i.name} — ${formatRupees(i.unitPrice)}${i.unitLabel ? ` / ${i.unitLabel}` : ""}`,
    })),
    { key: OTHER, value: OTHER, text: "Something else (type it in)…" },
  ];

  return (
    <Form onSubmit={submit} size="small">
      <Form.Group widths="equal">
        <Form.Select id="extra-choice" label="Add a charge" placeholder="Choose…" search options={options}
          value={form.choice} onChange={set("choice")} />
        <Form.Input id="extra-qty" label="Quantity" inputMode="decimal" value={form.quantity}
          error={!isQuantity(form.quantity)} onChange={set("quantity")} />
      </Form.Group>
      {other && (
        <>
          <Form.Group widths="equal">
            <Form.Input id="extra-desc" label="Description" placeholder="e.g. Laundry" value={form.description}
              onChange={set("description")} />
            <Form.Input id="extra-price" label="Price before GST (₹)" inputMode="decimal" value={form.unitPrice}
              error={form.unitPrice !== "" && !isMoney(form.unitPrice)} onChange={set("unitPrice")} />
            <Form.Input id="extra-heading" label="Heading (optional)" placeholder="Other" value={form.categoryName}
              onChange={set("categoryName")} />
          </Form.Group>
          <Form.Checkbox id="extra-gst" label="Charge GST (never on official bookings)"
            checked={form.gstApplicable} onChange={set("gstApplicable")} />
        </>
      )}
      <Button type="submit" size="small" disabled={!valid || busy}><Icon name="plus" />Add charge</Button>
    </Form>
  );
}
